import React, { useState, useRef, ChangeEvent } from 'react';
import { Button, Image } from 'react-bootstrap';
import FormError from '../../ui/FormError';

interface ImageUploadProps {
  initialImage?: string;
  onImageChange?: (file: File) => void;
  className?: string;
  height?: number;
  width?: number;
  placeholder?: string;
  name?: string;
  error?: string;
  accept?: string[];
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  initialImage,
  onImageChange,
  className = '',
  height = 100,
  width = 200,
  placeholder = 'Click to upload image',
  name,
  error,
  accept,
  ...props
}) => {
  const [previewUrl, setPreviewUrl] = useState<string>(initialImage || '');
  const [isHovered, setIsHovered] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (file) {
      // Check file size (5MB = 5 * 1024 * 1024 bytes)
      if (file.size > 5 * 1024 * 1024) {
        setErrorMessage('File size must be less than 5MB');
        return;
      }

      if (accept?.includes('image/*') || accept?.includes(file.type)) {
        const reader = new FileReader();

        reader.onloadend = () => {
          setPreviewUrl(reader.result as string);
        };

        reader.readAsDataURL(file);
        onImageChange?.(file);
      } else {
        setErrorMessage(
          `Unsupported file type. Please upload a ${accept
            ?.map(type => type.replace('image/', ''))
            .join(' or ')} file.`,
        );
      }
    }
  };

  const handleReplaceClick = () => {
    fileInputRef.current?.click();
    setErrorMessage(null);
  };

  return (
    <div style={{ width: width }}>
      <div
        className={`position-relative h-100 w-100 ${className}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <input
          name={name}
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept={accept?.join(',')}
          className="d-none"
        />

        {previewUrl ? (
          <div className="h-100 w-100 position-relative">
            <Image
              src={previewUrl}
              alt="Preview"
              className="rounded-3 w-100 h-100"
              style={{ objectFit: 'cover' }}
            />
          </div>
        ) : (
          <div
            className="rounded-3 w-100 h-100 d-flex flex-column align-items-center justify-content-center border border-1 border-dashed"
            style={{ cursor: 'pointer' }}
            onClick={handleReplaceClick}
          >
            <span className="p-3">{placeholder}</span>
          </div>
        )}
      </div>

      {previewUrl && (
        <Button
          variant="plain"
          size="sm"
          className="text-primary w-100 text-end"
          onClick={handleReplaceClick}
        >
          Replace Image
        </Button>
      )}
      {error && <FormError message={error} />}
      {errorMessage && <FormError message={errorMessage} />}
    </div>
  );
};

export default ImageUpload;
